import { useReducer, useEffect, useCallback, type ReactNode } from 'react';
import type { CartItem, Order, CartState, CartAction, CartContextValue } from '@/types';
import {
  getCartItems,
  saveCartItem,
  removeCartItem as removeCartItemDB,
  clearCart as clearCartDB,
  getOrders,
  saveOrder,
  updateOrderStatus,
  downloadOrderDesign,
  cleanupAbandonedOrders,
  migrateCartToUser,
} from '@/services/storage';
import { compressImageBlob } from '@/services/imageCompression';
import {
  syncCart,
  uploadCartItemToCloud,
  deleteCartItemFromCloud,
  clearCartFromCloud,
} from '@/services/supabaseSync';
import {
  fetchOrders as fetchSupabaseOrders,
  syncOrderStatuses as syncOrderStatusesAPI,
} from '@/services/orderService';
import type { SupabaseOrder } from '@/services/orderService';
import { getPaymentLink } from '@/config/stripe';
import { supabase, isSupabaseConfigured } from '@/config/supabase';
import { CartContext } from './CartContext';
import { useAuthSafe } from './useAuth';

const initialState: CartState = {
  cartItems: [],
  orders: [],
  isLoading: true,
  isAddingToCart: false,
  isDownloading: false,
  error: null,
};

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'SET_CART_ITEMS':
      return { ...state, cartItems: action.payload };
    case 'ADD_CART_ITEM':
      return { ...state, cartItems: [...state.cartItems, action.payload] };
    case 'REMOVE_CART_ITEM':
      return {
        ...state,
        cartItems: state.cartItems.filter((item) => item.id !== action.payload),
      };
    case 'UPDATE_CART_ITEM': {
      const updatedItems = state.cartItems.map((item) =>
        item.id === action.payload.id ? { ...item, ...action.payload.updates } : item
      );
      return { ...state, cartItems: updatedItems };
    }
    case 'SET_ORDERS':
      return { ...state, orders: action.payload };
    case 'ADD_ORDER':
      return { ...state, orders: [action.payload, ...state.orders] };
    case 'UPDATE_ORDER': {
      const updatedOrders = state.orders.map((order) =>
        order.id === action.payload.id ? { ...order, ...action.payload.updates } : order
      );
      return { ...state, orders: updatedOrders };
    }
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ADDING_TO_CART':
      return { ...state, isAddingToCart: action.payload };
    case 'SET_DOWNLOADING':
      return { ...state, isDownloading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'CLEAR_CART':
      return { ...state, cartItems: [] };
    default:
      return state;
  }
}

export interface CartProviderProps {
  children: ReactNode;
  openCart?: () => void;
}

const noop = () => {};

export function CartProvider({ children, openCart: openCartProp }: CartProviderProps) {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const auth = useAuthSafe();
  const user = auth?.user ?? null;
  const authLoading = auth?.isLoading ?? true;

  // Load cart and orders from IndexedDB on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        // Clean up abandoned orders before loading
        const cleanedCount = await cleanupAbandonedOrders();
        if (cleanedCount > 0) {
          console.warn(`Cleaned up ${cleanedCount} abandoned order(s)`);
        }

        const [cartItems, orders] = await Promise.all([getCartItems(), getOrders()]);
        dispatch({ type: 'SET_CART_ITEMS', payload: cartItems });
        dispatch({ type: 'SET_ORDERS', payload: orders });
      } catch (error) {
        console.error('Failed to load cart data:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to load cart data' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };
    loadData();
  }, []);

  // NOTE: Cart auto-save removed (Phase 3)
  // Cart operations now save to IndexedDB FIRST before updating state
  // This prevents the optimistic UI issues and ensures data consistency
  // See: addToCart, removeFromCart, updateQuantity functions

  // Sync cart to cloud before user logs out, then clear local cart
  useEffect(() => {
    // Skip if Supabase is not configured
    if (!isSupabaseConfigured()) return;

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        // Clear cart from IndexedDB and state (cloud already has the items)
        try {
          await clearCartDB();
          dispatch({ type: 'CLEAR_CART' });
          console.warn('[Cart] Cleared local cart on sign-out');
        } catch (error) {
          console.error('Failed to clear cart on logout:', error);
        }
      } else if (event === 'TOKEN_REFRESHED' && session?.user?.id) {
        // When token is refreshed, ensure cart is synced
        // This catches the case where user was offline when items were added
        try {
          const syncResult = await syncCart(session.user.id);
          if (syncResult.uploaded > 0) {
            console.warn('[Cart] Synced pending items on token refresh:', syncResult);
          }
        } catch (error) {
          console.warn('[Cart] Background sync failed:', error);
        }
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Migrate and sync cart when user signs in
  useEffect(() => {
    // Wait for auth to finish loading before checking user state
    if (authLoading) {
      return;
    }

    if (!user?.id || !isSupabaseConfigured()) {
      // Only log when auth is done loading to avoid misleading messages
      console.warn('[Cart] Skipping sync - user:', !!user?.id, 'supabase:', isSupabaseConfigured());
      return;
    }

    const migrateAndSyncCart = async () => {
      console.warn('[Cart] Starting migration and sync for user:', user.id);

      try {
        // 1. Get current local cart state
        const localItems = await getCartItems();
        console.warn(
          '[Cart] Local cart items:',
          localItems.length,
          localItems.map((i) => ({ id: i.id, userId: i.userId }))
        );

        // 2. Migrate orphan cart items (items without userId) to this user
        const migratedCount = await migrateCartToUser(user.id);
        if (migratedCount > 0) {
          console.warn(`[Cart] Migrated ${migratedCount} orphan cart items to user`);
        }

        // 3. Sync with cloud (bidirectional)
        console.warn('[Cart] Starting cloud sync...');
        const syncResult = await syncCart(user.id);
        console.warn('[Cart] Cloud sync result:', syncResult);

        // 4. Reload cart items from IndexedDB after sync
        const updatedCartItems = await getCartItems();
        console.warn('[Cart] Updated cart items after sync:', updatedCartItems.length);
        dispatch({ type: 'SET_CART_ITEMS', payload: updatedCartItems });
      } catch (error) {
        console.error('[Cart] Migration/sync failed:', error);
        // Don't set error state - this is a background operation
      }
    };

    migrateAndSyncCart();
  }, [user?.id, authLoading]);

  // Add item to cart
  // IMPORTANT: Save to IndexedDB FIRST, then update state (no optimistic UI)
  const addToCart = useCallback(
    async (item: Omit<CartItem, 'id' | 'addedAt'>) => {
      try {
        dispatch({ type: 'SET_ADDING_TO_CART', payload: true });

        // Compress images before storage (Phase 3 optimization)
        const compressedFrontBlob = item.frontDesignBlob
          ? await compressImageBlob(item.frontDesignBlob)
          : null;
        const compressedBackBlob = item.backDesignBlob
          ? await compressImageBlob(item.backDesignBlob)
          : null;

        const now = new Date();
        const cartItem: CartItem = {
          ...item,
          frontDesignBlob: compressedFrontBlob,
          backDesignBlob: compressedBackBlob,
          id: crypto.randomUUID(), // Use proper UUID for cloud sync compatibility
          userId: user?.id, // Link to user if signed in
          addedAt: now,
          updatedAt: now,
        };

        // Save to IndexedDB FIRST - if this fails, state is not updated
        await saveCartItem(cartItem);
        console.warn('[Cart] Item saved to IndexedDB:', cartItem.id);

        // Update state immediately after IndexedDB save succeeds
        dispatch({ type: 'ADD_CART_ITEM', payload: cartItem });

        // Cloud upload runs in background (fire-and-forget, sync will retry on failure)
        if (user?.id && isSupabaseConfigured()) {
          uploadCartItemToCloud(cartItem, user.id).catch((cloudError) => {
            console.warn('[Cart] Cloud upload failed (will sync later):', cloudError);
          });
        }
      } catch (error) {
        console.error('Failed to add to cart:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to add to cart' });
        throw error;
      } finally {
        dispatch({ type: 'SET_ADDING_TO_CART', payload: false });
      }
    },
    [user?.id]
  );

  // Remove item from cart
  // IMPORTANT: Delete from IndexedDB FIRST, then update state (no optimistic UI)
  const removeFromCart = useCallback(
    async (itemId: string) => {
      try {
        // Delete from IndexedDB FIRST - if this fails, state is not updated
        await removeCartItemDB(itemId);

        // Update state immediately after IndexedDB delete succeeds
        dispatch({ type: 'REMOVE_CART_ITEM', payload: itemId });

        // Cloud delete runs in background (fire-and-forget)
        if (user?.id && isSupabaseConfigured()) {
          deleteCartItemFromCloud(itemId).catch((cloudError) => {
            console.warn('[Cart] Cloud delete failed:', cloudError);
          });
        }
      } catch (error) {
        console.error('Failed to remove from cart:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to remove from cart' });
        throw error;
      }
    },
    [user?.id]
  );

  // Update cart item quantity
  // IMPORTANT: Save to IndexedDB FIRST, then update state (no optimistic UI)
  const updateQuantity = useCallback(
    async (itemId: string, quantity: number) => {
      const item = state.cartItems.find((i) => i.id === itemId);
      if (!item) return;
      try {
        const now = new Date();
        const updatedItem: CartItem = {
          ...item,
          quantity,
          updatedAt: now,
        };

        // Save to IndexedDB FIRST - if this fails, state is not updated
        await saveCartItem(updatedItem);

        // Only update state after successful save
        dispatch({
          type: 'UPDATE_CART_ITEM',
          payload: { id: itemId, updates: { quantity, updatedAt: now } },
        });
      } catch (error) {
        console.error('Failed to update quantity:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to update quantity' });
        throw error;
      }
    },
    [state.cartItems]
  );

  // Create orders from cart items (one order per cart item / design pair)
  const createOrder = useCallback(async (cartItems: CartItem[]): Promise<Order[]> => {
    const orders: Order[] = [];

    for (const cartItem of cartItems) {
      const order: Order = {
        id: `order-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        productId: cartItem.productId,
        productName: cartItem.productName,
        frontDesignBlob: cartItem.frontDesignBlob,
        backDesignBlob: cartItem.backDesignBlob,
        designThumbnail: cartItem.designThumbnail,
        quantity: cartItem.quantity,
        totalPaid: cartItem.priceInCents,
        status: 'pending',
        createdAt: new Date(),
        metadata: cartItem.metadata,
      };

      await saveOrder(order);
      dispatch({ type: 'ADD_ORDER', payload: order });
      orders.push(order);
    }

    return orders;
  }, []);

  // Initiate checkout flow
  const initiateCheckout = useCallback(async () => {
    if (state.cartItems.length === 0) {
      throw new Error('Cart is empty');
    }

    try {
      // Get Stripe Payment Link for the first product (assuming all same product for now)
      const firstItem = state.cartItems[0];
      if (!firstItem) {
        throw new Error('Cart is empty');
      }
      const paymentLink = getPaymentLink(firstItem.productId);
      if (!paymentLink) {
        throw new Error('Payment link not configured for this product');
      }

      // Create pending orders (one per cart item)
      await createOrder(state.cartItems);

      // Redirect to Stripe Payment Link
      window.location.href = paymentLink;
    } catch (error) {
      console.error('Checkout failed:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Checkout failed' });
      throw error;
    }
  }, [state.cartItems, createOrder]);

  // Confirm order after payment
  const confirmOrder = useCallback(async (orderId: string, stripePaymentIntentId?: string) => {
    try {
      await updateOrderStatus(orderId, 'paid', stripePaymentIntentId);

      // Update local state
      dispatch({
        type: 'UPDATE_ORDER',
        payload: {
          id: orderId,
          updates: {
            status: 'paid',
            paidAt: new Date(),
            stripePaymentIntentId,
          },
        },
      });

      // Clear cart after successful payment
      await clearCartDB();
      dispatch({ type: 'CLEAR_CART' });
    } catch (error) {
      console.error('Failed to confirm order:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to confirm order' });
      throw error;
    }
  }, []);

  // Cancel order
  const cancelOrder = useCallback(async (orderId: string) => {
    try {
      await updateOrderStatus(orderId, 'cancelled');
      dispatch({
        type: 'UPDATE_ORDER',
        payload: { id: orderId, updates: { status: 'cancelled' } },
      });
    } catch (error) {
      console.error('Failed to cancel order:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to cancel order' });
      throw error;
    }
  }, []);

  // Convert Supabase order to local Order format
  const convertSupabaseOrder = useCallback((so: SupabaseOrder): Order => {
    const firstItem = so.order_items[0];
    return {
      id: so.id,
      stripePaymentIntentId: so.stripe_session_id,
      productId: firstItem?.product_id || '',
      productName: firstItem?.product_name || 'Unknown Product',
      // For Supabase orders, we use URLs instead of blobs
      frontDesignBlob: null,
      backDesignBlob: null,
      designThumbnail: firstItem?.front_design_url || undefined,
      quantity: so.order_items.reduce((sum, item) => sum + item.quantity, 0),
      totalPaid: so.total_amount_cents,
      status: so.status as 'pending' | 'paid' | 'failed' | 'cancelled',
      createdAt: new Date(so.created_at),
      paidAt: so.paid_at ? new Date(so.paid_at) : undefined,
      metadata: {
        mode: (firstItem?.design_mode as 'library' | 'manual' | 'ai') || 'manual',
        designName: firstItem?.design_name || undefined,
        customerEmail: so.customer_email,
      },
      // Extended fields for Supabase orders
      orderNumber: so.order_number || undefined,
      frontDesignUrl: firstItem?.front_design_url || undefined,
      backDesignUrl: firstItem?.back_design_url || undefined,
      // Production status tracking fields
      productionStatus: (so.production_status as Order['productionStatus']) || undefined,
      productionStatusUpdatedAt: so.production_status_updated_at
        ? new Date(so.production_status_updated_at)
        : undefined,
      queuePosition: so.queue_position ?? undefined,
      estimatedDeliveryDate: so.estimated_delivery_date
        ? new Date(so.estimated_delivery_date)
        : undefined,
    };
  }, []);

  // Refresh orders - try Supabase first for authenticated users, then IndexedDB
  const refreshOrders = useCallback(async () => {
    try {
      // Try Supabase first for authenticated users
      if (user) {
        try {
          const supabaseOrders = await fetchSupabaseOrders();
          if (supabaseOrders.length > 0) {
            const orders = supabaseOrders.map(convertSupabaseOrder);
            dispatch({ type: 'SET_ORDERS', payload: orders });
            return;
          }
        } catch (error) {
          console.warn('Failed to fetch from Supabase, falling back to IndexedDB:', error);
        }
      }

      // Fall back to IndexedDB
      const orders = await getOrders();
      dispatch({ type: 'SET_ORDERS', payload: orders });
    } catch (error) {
      console.error('Failed to refresh orders:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to refresh orders' });
      throw error;
    }
  }, [user, convertSupabaseOrder]);

  // Sync order statuses from Google Sheets
  const syncOrderStatuses = useCallback(async () => {
    if (!user) {
      // Can't sync without authentication
      return;
    }

    try {
      await syncOrderStatusesAPI();
      // Refresh orders to get updated status data
      await refreshOrders();
    } catch (error) {
      console.error('Failed to sync order statuses:', error);
      // Don't throw - sync failure shouldn't break the UI
    }
  }, [user, refreshOrders]);

  // Download design from order
  const downloadDesign = useCallback(async (orderId: string) => {
    try {
      dispatch({ type: 'SET_DOWNLOADING', payload: true });
      await downloadOrderDesign(orderId);
    } catch (error) {
      console.error('Failed to download design:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to download design' });
      throw error;
    } finally {
      dispatch({ type: 'SET_DOWNLOADING', payload: false });
    }
  }, []);

  // Get cart total in cents
  const getCartTotal = useCallback((): number => {
    return state.cartItems.reduce((total, item) => total + item.priceInCents * item.quantity, 0);
  }, [state.cartItems]);

  // Get cart item count
  const getItemCount = useCallback((): number => {
    return state.cartItems.length;
  }, [state.cartItems]);

  // Clear all items from cart
  const clearCart = useCallback(async () => {
    try {
      await clearCartDB();

      // Update state immediately after IndexedDB clear succeeds
      dispatch({ type: 'CLEAR_CART' });

      // Cloud clear runs in background (fire-and-forget)
      if (user?.id && isSupabaseConfigured()) {
        clearCartFromCloud(user.id).catch((cloudError) => {
          console.warn('[Cart] Cloud clear failed:', cloudError);
        });
      }
    } catch (error) {
      console.error('Failed to clear cart:', error);
      throw error;
    }
  }, [user?.id]);

  const value: CartContextValue = {
    cartItems: state.cartItems,
    orders: state.orders,
    isLoading: state.isLoading,
    isAddingToCart: state.isAddingToCart,
    isDownloading: state.isDownloading,
    error: state.error,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    initiateCheckout,
    createOrder,
    confirmOrder,
    cancelOrder,
    refreshOrders,
    syncOrderStatuses: user ? syncOrderStatuses : null,
    downloadDesign,
    getCartTotal,
    getItemCount,
    openCart: openCartProp ?? noop,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
